import React, { useEffect, useState, useRef } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const PortfolioPieChart = ({ 
  data, 
  valueKey = 'value', 
  nameKey = 'name', 
  title = 'Distribution', 
  colors = [
    '#2563eb',  // Primary blue
    '#10b981',  // Success green
    '#8b5cf6',  // Purple
    '#f59e0b',  // Amber
    '#ef4444',  // Red
    '#ec4899',  // Pink
    '#06b6d4',  // Cyan
    '#64748b',  // Secondary gray
    '#a855f7',  // Violet
    '#84cc16',  // Lime
    '#14b8a6',  // Teal
    '#f97316',  // Orange
    '#0ea5e9',  // Sky
    '#d946ef',  // Fuchsia
    '#facc15',  // Yellow
    '#6366f1'   // Indigo
  ],
  currencySymbol = '€',
  emptyMessage = 'No data available'
}) => {
  const [chartKey, setChartKey] = useState(Date.now());
  const containerRef = useRef(null);
  
  // This effect handles responsive resizing
  useEffect(() => {
    const handleResize = () => {
      // Force the ResponsiveContainer to re-render when window is resized
      setChartKey(Date.now());
    };
    
    window.addEventListener('resize', handleResize);
    
    // Clean up event listener
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-text-secondary">{emptyMessage}</p>
      </div>
    );
  }

  // Calculate total value
  const totalValue = data.reduce((sum, item) => sum + item[valueKey], 0);

  // Format the data for the pie chart
  const chartData = data.map((item) => ({
    name: item[nameKey],
    value: item[valueKey],
    percentage: ((item[valueKey] / totalValue) * 100).toFixed(1)
  })).sort((a, b) => b.value - a.value);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const color = payload[0].color;
      
      return (
        <div style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--border-color)',
          borderRadius: '12px',
          padding: '1rem',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.12)',
          minWidth: '200px',
          maxWidth: '300px',
          animation: 'tooltipFade 0.2s ease-in-out'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '0.75rem',
            borderBottom: '1px solid var(--border-color)',
            paddingBottom: '0.75rem'
          }}>
            <div 
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: color
              }}
            />
            <span style={{
              color: 'var(--text-primary)',
              fontSize: '0.9375rem',
              fontWeight: 500
            }}>{data.name}</span>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{
                color: 'var(--text-secondary)',
                fontSize: '0.8125rem'
              }}>Wert</span>
              <span style={{
                color: 'var(--text-primary)',
                fontSize: '0.9375rem',
                fontWeight: 500
              }}>
                {Number(data.value).toLocaleString('de-DE', {
                  style: 'currency',
                  currency: 'EUR',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                })}
              </span>
            </div>
            
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{
                color: 'var(--text-secondary)',
                fontSize: '0.8125rem'
              }}>Anteil</span>
              <span style={{
                color: 'var(--text-primary)',
                fontSize: '0.9375rem',
                fontWeight: 500
              }}>{data.percentage}%</span>
            </div>
          </div>
          
          <style>{`
            @keyframes tooltipFade {
              from {
                opacity: 0;
                transform: translateY(4px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
          `}</style>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="portfolio-chart" style={{ height: '100%', padding: 0 }} ref={containerRef}>
      <div style={{ 
        height: '100%',
        width: '100%',
        padding: 0,
        margin: 0
      }}>
        <ResponsiveContainer width="100%" height="100%" key={chartKey}>
          <PieChart margin={{ top: 0, right: 0, bottom: 10, left: 0 }}>
              <text
                x="50%"
                y="45%"
                textAnchor="middle"
                dominantBaseline="middle"
                style={{
                  fill: 'var(--text-primary)',
                  fontSize: '1.25rem',
                  fontWeight: 500,
                  letterSpacing: '-0.01em',
                }}
              >
                {title}
              </text>
              <text
                x="50%"
                y="55%"
                textAnchor="middle"
                dominantBaseline="middle"
                style={{
                  fill: 'var(--text-secondary)',
                  fontSize: '0.875rem',
                  fontWeight: 400,
                }}
              >
                {totalValue.toLocaleString('de-DE', {
                  style: 'currency',
                  currency: 'EUR',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                })}
              </text>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius="60%"
                outerRadius="90%"
                paddingAngle={3}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`}
                    fill={colors[index % colors.length]}
                    stroke="rgba(0,0,0,0.1)"
                    strokeWidth={2}
                  />
                ))}
              </Pie>
              <Tooltip 
                content={<CustomTooltip />} 
                cursor={false}
                wrapperStyle={{ outline: 'none' }}
              />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PortfolioPieChart;